import { object, ref, string, type InferType } from "yup";
import { isUSA } from "../../../../common/utils";
import { AddressInputSchemaGenerator } from "../../../SharedAddressInput/constants";
import { getNameFromCountry } from "../../mappers";

export enum BacsPaymentPrefInputFields {
  AccountName = "accountName",
  SortCode = "sortCode",
  BankName = "bankName",
  AccountNumber = "accountNumber",
  ConfirmAccountNumber = "confirmAccountNumber",
  AdditionalInstructions = "additionalInstructions",
  Address = "address",
  BankCountryValidatedCountry = "bankCountryValidatedCountry",
}

export const SORT_CODE_DIGITS = 6;
export const ACCOUNT_NUMBER_DIGITS = 8;
export const ADDITIONAL_INSTRUCTIONS_MAX_LENGTH = 35;

const stripSortCodeDashes = (value: string) => value.replace(/-/g, "");

export const BacsPaymentPrefInputSchema = object()
  .shape({
    [BacsPaymentPrefInputFields.AccountName]: string()
      .required("Required field")
      .matches(/\S/, "Account name cannot be just spaces"),
    [BacsPaymentPrefInputFields.SortCode]: string()
      .required("Required field")
      .test("sort-code-digits", `Sort code must be ${SORT_CODE_DIGITS} digits`, (value) => {
        if (!value) return false;
        return stripSortCodeDashes(value).length === SORT_CODE_DIGITS;
      })
      .test("sort-code-numeric", "Sort code must contain only digits", (value) => {
        if (!value) return false;
        return /^[0-9]+$/.test(stripSortCodeDashes(value));
      }),
    [BacsPaymentPrefInputFields.BankName]: string()
      .required("Required field")
      .matches(/\S/, "Bank name cannot be just spaces."),
    [BacsPaymentPrefInputFields.AccountNumber]: string()
      .required("Required field")
      .length(ACCOUNT_NUMBER_DIGITS, `Account number must be ${ACCOUNT_NUMBER_DIGITS} digits`)
      .matches(/^[0-9]+$/, "Account number must contain only digits"),
    [BacsPaymentPrefInputFields.ConfirmAccountNumber]: string()
      .oneOf([ref(BacsPaymentPrefInputFields.AccountNumber)], "Must match account number")
      .required("Required field"),
    [BacsPaymentPrefInputFields.AdditionalInstructions]: string().max(
      ADDITIONAL_INSTRUCTIONS_MAX_LENGTH,
      `Must be ${ADDITIONAL_INSTRUCTIONS_MAX_LENGTH} characters or less`,
    ),
    [BacsPaymentPrefInputFields.BankCountryValidatedCountry]: string().optional(),
  })
  .concat(
    object().shape({
      [BacsPaymentPrefInputFields.Address]: AddressInputSchemaGenerator({
        testIsUSA: (country) => isUSA(getNameFromCountry(country)),
        zipRequiredForNonUS: false,
      }),
    }),
  );

export type BacsPaymentPrefInputType = InferType<typeof BacsPaymentPrefInputSchema>;
